import { useState, useEffect } from 'react';
import { Shield, Mail, Smartphone, Key, Trash2, Loader2, RefreshCw, Monitor, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = '';

// All admin endpoints accept the OAuth session_token cookie via credentials:'include'
// post-cutover (Phase 2). Bearer-from-localStorage path is removed.
const adminFetch = (url, options = {}) => fetch(url, { ...options, credentials: 'include' });

export default function AdminSecurity() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changingMethod, setChangingMethod] = useState(false);
  const [newMethod, setNewMethod] = useState('');
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenCodes, setRegenCodes] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logFilter, setLogFilter] = useState('');
  const [tab, setTab] = useState('settings');

  const fetchSettings = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/2fa-settings`);
      if (res.ok) {
        setSettings(await res.json());
        setError('');
      } else {
        const data = await res.json().catch(() => ({}));
        console.error('AdminSecurity 2fa-settings failed:', res.status, data);
        setError(data.detail || 'Impossible de charger les paramètres de sécurité. Reconnecte-toi si le problème persiste.');
      }
    } catch (err) {
      console.error('AdminSecurity fetch failed:', err);
      setError('Impossible de charger les paramètres de sécurité. Reconnecte-toi si le problème persiste.');
    } finally { setLoading(false); }
  };

  const fetchLogs = async (filter) => {
    try {
      const url = filter ? `${API_URL}/api/admin/audit-log?action=${filter}` : `${API_URL}/api/admin/audit-log`;
      const res = await adminFetch(url);
      if (res.ok) { const data = await res.json(); setAuditLogs(data.logs || []); }
      else {
        const data = await res.json().catch(() => ({}));
        console.error('AdminSecurity audit-log failed:', res.status, data);
        toast.error(data.detail || 'Impossible de charger le journal');
      }
    } catch (err) {
      console.error('AdminSecurity audit-log fetch failed:', err);
      toast.error('Erreur de connexion au serveur');
    }
  };

  useEffect(() => { fetchSettings(); fetchLogs(); }, []);

  const handleChangeMethod = async () => {
    if (!newMethod) { toast.error('Choisis une methode'); return; }
    setActionLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/2fa-settings/change-method`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: newMethod }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Methode 2FA changee');
        setChangingMethod(false);
        fetchSettings();
        if (data.totp_qr) {
          toast.info('Scanne le nouveau QR code avec ton application');
        }
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) { console.error('change-method failed:', err); toast.error('Erreur'); }
    finally { setActionLoading(false); }
  };

  const handleRegenerateCodes = async () => {
    setActionLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/2fa-settings/regenerate-codes`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok) {
        setRegenCodes(data.codes);
        setShowRegenModal(false);
        toast.success('Codes regeneres');
        fetchSettings();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) { console.error('regenerate-codes failed:', err); toast.error('Erreur'); }
    finally { setActionLoading(false); }
  };

  const handleRevokeDevices = async () => {
    if (!window.confirm('Revoquer tous les appareils de confiance ?')) return;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/2fa-settings/revoke-devices`, { method: 'POST' });
      if (res.ok) { toast.success('Appareils revoques'); fetchSettings(); }
      else { toast.error('Erreur lors de la revocation'); }
    } catch (err) { console.error('revoke-devices failed:', err); toast.error('Erreur'); }
  };

  const logColors = {
    LOGIN_SUCCESS: 'text-green-600', LOGIN_FAILED: 'text-red-600', LOGIN_BLOCKED: 'text-red-700',
    '2FA_SUCCESS': 'text-green-600', '2FA_FAILED': 'text-yellow-600', '2FA_RECOVERY_USED': 'text-red-600',
    LOGOUT: 'text-gray-500', DEVICES_REVOKED: 'text-yellow-600',
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div data-testid="admin-security-page">
      <h1 className="text-2xl font-serif font-bold mb-6" data-testid="security-title">Securite & Authentification</h1>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6" data-testid="security-error">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-1 border-b border-[#E0DFDD] mb-6">
        <button onClick={() => setTab('settings')} className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px', tab === 'settings' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68]')} data-testid="tab-settings">
          <Shield size={14} className="inline mr-1.5" /> Parametres 2FA
        </button>
        <button onClick={() => { setTab('audit'); fetchLogs(logFilter); }} className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px', tab === 'audit' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68]')} data-testid="tab-audit">
          <Key size={14} className="inline mr-1.5" /> Journal d'activite
        </button>
      </div>

      {tab === 'settings' && settings && (
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-white border border-[#E0DFDD] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-3 h-3 rounded-full', settings.tfa_enabled ? 'bg-green-500' : 'bg-red-500')} />
                <div>
                  <p className="font-medium text-sm">2FA {settings.tfa_enabled ? 'Active' : 'Desactive'}</p>
                  <p className="text-xs text-[#6B6A68]">Methode : {settings.tfa_method === 'totp' ? 'Application Authenticator' : 'Code par Email'}</p>
                </div>
              </div>
              <button onClick={() => { setChangingMethod(true); setNewMethod(settings.tfa_method === 'email' ? 'totp' : 'email'); }} className="text-sm text-[#D4AF37] hover:underline" data-testid="change-method-btn">Changer de methode</button>
            </div>
          </div>

          {/* Change method */}
          {changingMethod && (
            <div className="bg-white border border-[#E0DFDD] p-5 space-y-4">
              <h3 className="font-medium text-sm">Changer la methode 2FA</h3>
              <div className="flex gap-2">
                <button onClick={() => setNewMethod('email')} className={cn('flex-1 p-3 border text-sm', newMethod === 'email' ? 'border-[#1A1514] bg-[#F9F8F6]' : 'border-[#E0DFDD]')} data-testid="method-email"><Mail size={16} className="inline mr-2" />Email</button>
                <button onClick={() => setNewMethod('totp')} className={cn('flex-1 p-3 border text-sm', newMethod === 'totp' ? 'border-[#1A1514] bg-[#F9F8F6]' : 'border-[#E0DFDD]')} data-testid="method-totp"><Smartphone size={16} className="inline mr-2" />Authenticator</button>
              </div>
              <p className="text-xs text-[#6B6A68] bg-[#F9F8F6] border border-[#E0DFDD] p-3" data-testid="change-method-notice">
                Ta session admin est deja verifiee (Google + 2FA). Confirme pour appliquer le changement de methode.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setChangingMethod(false)} className="flex-1 border border-[#E0DFDD] py-2.5 text-sm" data-testid="cancel-method-btn">Annuler</button>
                <button onClick={handleChangeMethod} disabled={actionLoading || !newMethod} className="flex-1 bg-[#1A1514] text-white py-2.5 text-sm disabled:opacity-50" data-testid="save-method-btn">{actionLoading ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Confirmer'}</button>
              </div>
            </div>
          )}

          {/* Recovery codes */}
          <div className="bg-white border border-[#E0DFDD] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Codes de recuperation</p>
                <p className="text-xs text-[#6B6A68]">{settings.recovery_codes_remaining} codes restants sur 8</p>
              </div>
              <button onClick={() => setShowRegenModal(true)} className="flex items-center gap-1 text-sm text-[#D4AF37] hover:underline" data-testid="regen-codes-btn"><RefreshCw size={14} /> Regenerer</button>
            </div>
            {settings.recovery_codes_remaining <= 2 && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs p-2">Attention : il ne reste que {settings.recovery_codes_remaining} codes. Regenere-les rapidement.</div>
            )}
          </div>

          {/* Regenerated codes display */}
          {regenCodes && (
            <div className="bg-white border border-[#E0DFDD] p-5">
              <h3 className="font-medium text-sm mb-3">Nouveaux codes de recuperation</h3>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {regenCodes.map((c, i) => <span key={i} className="font-mono text-sm text-center bg-[#F9F8F6] p-2 border border-[#E0DFDD]">{c}</span>)}
              </div>
              <button onClick={() => setRegenCodes(null)} className="w-full border border-[#E0DFDD] py-2 text-sm">J'ai sauvegarde mes codes</button>
            </div>
          )}

          {/* Regen modal */}
          {showRegenModal && (
            <div className="bg-white border border-red-300 p-5 space-y-3" data-testid="regen-modal">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm" data-testid="regen-warning">
                <AlertTriangle size={16} /> Action irreversible
              </div>
              <p className="text-sm text-[#1A1514] leading-relaxed" data-testid="regen-modal-body">
                Tu vas generer 8 nouveaux codes de recuperation. Les anciens seront immediatement invalides. Sauvegarde les nouveaux codes immediatement — ils ne seront affiches qu'une fois.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setShowRegenModal(false)} className="flex-1 border border-[#E0DFDD] py-2.5 text-sm" data-testid="regen-cancel">Annuler</button>
                <button onClick={handleRegenerateCodes} disabled={actionLoading} className="flex-1 bg-[#1A1514] text-white py-2.5 text-sm disabled:opacity-50" data-testid="confirm-regen">{actionLoading ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Generer les codes'}</button>
              </div>
            </div>
          )}

          {/* Trusted devices */}
          <div className="bg-white border border-[#E0DFDD] p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium text-sm">Appareils de confiance</p>
              {settings.trusted_devices?.length > 0 && (
                <button onClick={handleRevokeDevices} className="flex items-center gap-1 text-sm text-red-500 hover:underline" data-testid="revoke-devices"><Trash2 size={14} /> Tout revoquer</button>
              )}
            </div>
            {!settings.trusted_devices?.length ? (
              <p className="text-xs text-[#6B6A68]">Aucun appareil de confiance.</p>
            ) : (
              <div className="space-y-2">
                {settings.trusted_devices.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#F9F8F6] p-3 text-xs">
                    <div className="flex items-center gap-2"><Monitor size={14} className="text-[#6B6A68]" /><span>{d.user_agent?.slice(0, 50)}...</span></div>
                    <div className="text-[#6B6A68]">{d.ip} | {d.created_at?.split('T')[0]}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'settings' && !settings && !error && !loading && (
        <div className="text-center text-sm text-[#6B6A68] py-12 border border-dashed border-[#E0DFDD]" data-testid="security-no-data">
          Aucune donnée disponible. Reconnecte-toi à l'admin si le problème persiste.
        </div>
      )}

      {/* Audit Log */}
      {tab === 'audit' && (
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {['', 'LOGIN_SUCCESS', 'LOGIN_FAILED', '2FA_FAILED', '2FA_RECOVERY_USED', 'LOGOUT'].map(f => (
              <button key={f} onClick={() => { setLogFilter(f); fetchLogs(f); }} className={cn('px-3 py-1.5 text-xs border', logFilter === f ? 'border-[#1A1514] bg-[#1A1514] text-white' : 'border-[#E0DFDD]')} data-testid={`log-filter-${f || 'all'}`}>
                {f || 'Tous'}
              </button>
            ))}
          </div>
          <div className="bg-white border border-[#E0DFDD] overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-[#F9F8F6]">
                <th className="text-left p-3 font-medium">Action</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-left p-3 font-medium">IP</th>
                <th className="text-left p-3 font-medium">Date</th>
              </tr></thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-[#6B6A68]">Aucun log</td></tr>
                ) : auditLogs.map((log, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-[#F9F8F6]" data-testid={`log-row-${i}`}>
                    <td className={cn('p-3 font-medium', logColors[log.action] || 'text-gray-600')}>{log.action}</td>
                    <td className="p-3 text-[#6B6A68]">{log.admin_id || log.user_id || '-'}</td>
                    <td className="p-3 font-mono text-[#6B6A68]">{log.ip || '-'}</td>
                    <td className="p-3 text-[#6B6A68]">{log.timestamp?.replace('T', ' ').slice(0, 19)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
